import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import Swal from "sweetalert2";

const UploadContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "hasFile" && prop !== "disabled" && prop !== "isDragging",
})(({ hasFile, disabled, isDragging }) => ({
  width: "250px",
  height: "250px",
  border: `2px dashed ${
    disabled ? "#ccc" : isDragging ? "#2a9941" : hasFile ? "#2a9941" : "#d2d6da"
  }`,
  borderRadius: "15px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  cursor: disabled ? "not-allowed" : "pointer",
  position: "relative",
  overflow: "hidden",
  backgroundColor: disabled ? "#f0f0f0" : isDragging ? "#f0fff0" : "transparent",
  transition: "all 0.3s ease-in-out",
  padding: "10px",
  textAlign: "center",
}));

const ImagePreview = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "15px",
});

const PdfPreview = styled("iframe")({
  width: "100%",
  height: "100%",
  border: "none",
});

const WordPlaceholder = styled(Box)({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  color: "#2a9941",
  textAlign: "center",
  padding: "10px",
});

const HiddenInput = styled("input")({
  display: "none",
});

const FileSelector = ({ onFileSelect, fileUrl, disabled }) => {
  const [previewUrl, setPreviewUrl] = useState(fileUrl || null);
  const [fileType, setFileType] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const inputRef = useRef();

  useEffect(() => {
    if (fileUrl) {
      setPreviewUrl(fileUrl);
      if (fileUrl.endsWith(".pdf")) setFileType("pdf");
      else if (fileUrl.endsWith(".doc") || fileUrl.endsWith(".docx")) setFileType("word");
      else setFileType("image");
    }
  }, [fileUrl]);

  const handleFile = (file) => {
    const fileSizeLimit = 1 * 1024 * 1024; // 1MB

    if (file.size > fileSizeLimit) {
      Swal.fire({
        icon: "error",
        title: "File too large!",
        text: "Please upload a file smaller than 1MB.",
        confirmButtonColor: "#d33",
      });
      return;
    }

    const type = file.type;
    const objectUrl = URL.createObjectURL(file);
    setFileName(file.name);

    if (type.startsWith("image/")) {
      setIsLoading(true);
      const img = new Image();
      img.src = objectUrl;
      img.onload = () => {
        setFileType("image");
        setPreviewUrl(objectUrl);
        onFileSelect(file);
        setIsLoading(false);
      };
      img.onerror = () => {
        setIsLoading(false);
        Swal.fire({
          icon: "error",
          title: "Invalid image",
          text: "Something went wrong while loading the image.",
        });
      };
    } else if (type === "application/pdf") {
      setFileType("pdf");
      setPreviewUrl(objectUrl);
      onFileSelect(file);
    } else if (
      type === "application/msword" ||
      type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      setFileType("word");
      setPreviewUrl(objectUrl);
      onFileSelect(file);
    } else {
      Swal.fire({
        icon: "error",
        title: "Unsupported file",
        text: "Only image, PDF, and Word files are allowed.",
      });
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && !disabled) {
      handleFile(file);
    }
  };

  const handleRemoveFile = (e) => {
    if (disabled) return;
    e.stopPropagation();
    setPreviewUrl(null);
    setFileType(null);
    setFileName(null);
    onFileSelect(null);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (disabled) return;
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  return (
    <Box>
      <UploadContainer
        onClick={() => !disabled && inputRef.current.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        disabled={disabled}
        hasFile={!!previewUrl}
        isDragging={isDragging}
      >
        {isLoading ? (
          <CircularProgress sx={{ color: "#2a9941" }} />
        ) : previewUrl ? (
          <>
            {fileType === "image" && (
              <ImagePreview src={previewUrl} alt="Selected" />
            )}
            {fileType === "pdf" && (
              <>
                <PdfPreview src={previewUrl} title="PDF Preview" />
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    bottom: 8,
                    backgroundColor: "#ffffffaa",
                    padding: "2px 6px",
                    borderRadius: "6px",
                    fontSize: "12px",
                  }}
                >
                  {fileName}
                </Typography>
              </>
            )}
            {fileType === "word" && (
              <WordPlaceholder>
                <DescriptionIcon sx={{ fontSize: 50, color: "#2a9941" }} />
                <Typography variant="subtitle2" mt={1}>
                  {fileName}
                </Typography>
              </WordPlaceholder>
            )}
            {!disabled && (
              <IconButton
                sx={{
                  position: "absolute",
                  top: 5,
                  right: 5,
                  background: "#3F51B5d4",
                  color: "#fff",
                  "&:hover": { background: "#1ca444" },
                }}
                onClick={handleRemoveFile}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </>
        ) : (
          <>
            <PhotoCameraIcon
              sx={{ fontSize: 50, color: disabled ? "#aaa" : "#2a9941" }}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: disabled ? "#aaa" : "#666",
                textAlign: "center",
              }}
            >
              {disabled
                ? "Disabled"
                : "Click or Drag & Drop to select image, PDF or Word file"}
              <br />
              {!disabled && (
                <Typography
                  variant="caption"
                  sx={{ mt: 1, color: "#666", textAlign: "center" }}
                >
                  Max file size: 1MB
                </Typography>
              )}
            </Typography>
          </>
        )}
      </UploadContainer>

      <HiddenInput
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={handleFileChange}
        disabled={disabled}
      />
    </Box>
  );
};

export default FileSelector;
